/**
 * WordPress REST API client for GetMD.
 *
 * @package GetMD
 * @since   1.0.0
 */

import type { FilterCriteria, ExportJob, DatabaseHealth, UserSettings, UserSettingsInput, SaveSettingsResponse, GlobalSettings, SaveGlobalSettingsResponse } from '../types/wordpress';

/**
 * API client for WordPress REST API.
 */
class APIClient {
  private baseUrl: string;
  private nonce: string;

  constructor() {
    // Validate window.summixGetmdData exists
    if (!window.summixGetmdData) {
      console.error('Missing WordPress export data. Plugin may not be properly initialized.');
      throw new Error('WordPress export data not found. Please refresh the page.');
    }

    const { apiUrl, nonce } = window.summixGetmdData;

    // Validate required nonce
    if (!nonce) {
      console.error('Missing authentication nonce. Cannot make authenticated requests.');
      throw new Error('Authentication nonce not found. Please refresh the page.');
    }

    this.baseUrl = apiUrl || '/wp-json/summix-getmd/v1';
    this.nonce = nonce;
  }

  /**
   * Fetch wrapper with WordPress nonce authentication.
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Handle URL construction properly when base URL contains query params
    // (e.g., when pretty permalinks are disabled: /index.php?rest_route=/namespace/v1)
    let url: string;
    if (this.baseUrl.includes('?')) {
      // Base URL has query params - use & for additional params
      const [path, query] = endpoint.split('?');
      url = `${this.baseUrl}${path}${query ? '&' + query : ''}`;
    } else {
      url = `${this.baseUrl}${endpoint}`;
    }

    let response: Response;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': this.nonce,
          ...options.headers,
        },
        credentials: 'same-origin',
        signal: controller.signal,
      });
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw new Error('Network error. Check your connection and try again.');
    } finally {
      clearTimeout(timeout);
    }

    if (!response.ok) {
      // Try to parse error response
      const error = await response.json().catch(() => ({
        message: response.statusText,
      }));

      // Provide user-friendly error messages based on status code
      let errorMessage = error.message || `API request failed (HTTP ${response.status})`;

      switch (response.status) {
        case 400:
          errorMessage = error.message || 'Invalid request. Please check your input.';
          break;
        case 401:
          errorMessage = 'Authentication failed. Please refresh the page and try again.';
          break;
        case 403:
          errorMessage = 'You do not have permission to perform this action.';
          break;
        case 404:
          errorMessage = 'The requested resource was not found.';
          break;
        case 500:
          errorMessage = error.message || 'Server error. Please try again later.';
          break;
        case 503:
          errorMessage = 'Service temporarily unavailable. Please try again later.';
          break;
      }

      const err = new Error(errorMessage) as Error & { status?: number; body?: unknown };
      err.status = response.status;
      err.body = error;
      throw err;
    }

    // Handle 204 No Content responses
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  /**
   * Create new export job.
   */
  async createExport(filters: FilterCriteria): Promise<ExportJob> {
    return this.request<ExportJob>('/export', {
      method: 'POST',
      body: JSON.stringify({ filters }),
    });
  }

  /**
   * Get export job status.
   */
  async getExportStatus(exportId: number): Promise<ExportJob> {
    return this.request<ExportJob>(`/export/${exportId}/status`);
  }

  /**
   * Get export preview count.
   * Returns the count of posts that would be exported with the given filters.
   */
  async getExportPreview(filters: FilterCriteria): Promise<{ count: number; post_types: string[] }> {
    return this.request<{ count: number; post_types: string[] }>('/export/preview', {
      method: 'POST',
      body: JSON.stringify({ filters }),
    });
  }

  /**
   * Get export history.
   */
  async getExportHistory(
    limit: number = 20,
    offset: number = 0
  ): Promise<ExportJob[]> {
    return this.request<ExportJob[]>(
      `/history?limit=${limit}&offset=${offset}`
    );
  }

  /**
   * Delete export job.
   */
  async deleteExport(exportId: number): Promise<{ message: string }> {
    return this.request<{ message: string }>(`/history/${exportId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get database health status.
   */
  async getDatabaseHealth(): Promise<DatabaseHealth> {
    return this.request<DatabaseHealth>('/database/health');
  }

  /**
   * Repair database tables.
   */
  async repairDatabase(): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/database/repair', {
      method: 'POST',
    });
  }

  /**
   * Get user settings.
   */
  async getSettings(): Promise<UserSettings> {
    return this.request<UserSettings>('/settings');
  }

  /**
   * Save user settings.
   */
  async saveSettings(settings: UserSettingsInput): Promise<SaveSettingsResponse> {
    return this.request<SaveSettingsResponse>('/settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  /**
   * Get global site settings (admin only).
   */
  async getGlobalSettings(): Promise<GlobalSettings> {
    return this.request<GlobalSettings>('/global-settings');
  }

  /**
   * Save global site settings (admin only).
   *
   * Note: All fields in GlobalSettings are required by the backend.
   * Partial updates are not supported.
   */
  async saveGlobalSettings(settings: GlobalSettings): Promise<SaveGlobalSettingsResponse> {
    // Validate required fields before sending
    if (settings.frontendViewEnabled === undefined) {
      throw new Error('frontendViewEnabled is required');
    }

    return this.request<SaveGlobalSettingsResponse>('/global-settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }
}

// Private singleton instance
let instance: APIClient | null = null;

/**
 * Get or create the API client instance.
 * Uses lazy initialization to avoid import-time crashes.
 */
export function getAPI(): APIClient {
  if (!instance) {
    instance = new APIClient();
  }
  return instance;
}
